import React, { useState, useEffect } from 'react'
import { DropdownButton, MenuItem } from 'react-bootstrap'
import { truncate } from 'lodash'

import isInExperiment, { InternalUserExperiment } from 'lib/isInExperiment'
import { RegistryProfileIcon } from 'components/icons'
import Image from 'components/image'
import { track, useTracking } from '../../../lib/analytics'
import * as urls from '../../../lib/urls'
import '../global-nav.scss'
import {
  INSTALLED_BABYLIST_EXTENSION_EVENT,
  UPDATE_NAVBAR_USER_ICON,
} from '../../../registry/constants'

interface AccountMenuProps {
  user: {
    addedBabylistButton: boolean
    currentRegistry: {
      photoUrl?: string
      urlSlug: string
    }
    firstName: string
    hasMultipleRegistries: boolean
    id: number
    experiments: InternalUserExperiment[]
  }
}

// This function already exists in registry/reducers/user-reducer but importing the reducer
// in this file causes circular dependency issues. Adding it here as a quick fix for the
// extension_button_adoption_experiment experiment. This can be removed when the experiment
// is resolved.
export const getBabylistExtensionInstalled = (user: any) =>
  user &&
  user.events?.some(
    (event: any) =>
      event.name === INSTALLED_BABYLIST_EXTENSION_EVENT && event.value === true
  )

export default function AccountMenu({ user }: AccountMenuProps) {
  const { trackEvent } = useTracking()
  const {
    currentRegistry,
    addedBabylistButton,
    id,
    firstName,
    hasMultipleRegistries,
  } = user

  const shouldShowInstallMenuItem =
    (!isInExperiment(
      user.experiments,
      'extension_button_adoption_experiment'
    ) &&
      !addedBabylistButton) ||
    (isInExperiment(user.experiments, 'extension_button_adoption_experiment') &&
      !getBabylistExtensionInstalled(user))

  const [photoUrl, setPhotoUrl] = useState(currentRegistry.photoUrl)

  useEffect(() => {
    const updatePhotoUrl = ({ newUrl }: { newUrl: string }) =>
      setPhotoUrl(newUrl)
    PubSub.subscribe(UPDATE_NAVBAR_USER_ICON, updatePhotoUrl)
    return () => PubSub.unsubscribe(UPDATE_NAVBAR_USER_ICON, updatePhotoUrl)
  }, [])

  const trackLogOut: React.MouseEventHandler<{}> = (e) => {
    e.preventDefault()
    trackEvent({ event: track.logOutCompleted })
    window.location.href = urls.logoutPath
  }

  const avatar = photoUrl ? (
    <Image
      className="mrs circle-mask"
      src={photoUrl}
      width={34}
      height={34}
      lazyLoad={false}
      role="presentation"
    />
  ) : (
    <span aria-label="Baby Icon" className="mrs circle-mask bas pas" role="img">
      <RegistryProfileIcon height={20} width={20} />
    </span>
  )
  const title = (
    <div className="flex-row pull-left">
      {avatar}
      <span className="mrm">{truncate(firstName, { length: 13 })}</span>
    </div>
  )

  return (
    <DropdownButton
      aria-label="my account"
      bsStyle="nav-link"
      className="hidden-xs"
      id="account-menu"
      title={title}
      key={id}
    >
      <MenuItem eventKey="1" href={urls.registryPath(currentRegistry.urlSlug)}>
        Your Registry
      </MenuItem>
      {shouldShowInstallMenuItem && (
        <MenuItem eventKey="2" href={urls.addItemsPath}>
          Install the Babylist Button
        </MenuItem>
      )}
      <MenuItem eventKey="3" href={urls.settingsPath}>
        Settings
      </MenuItem>

      <MenuItem eventKey="4" href={urls.settingsYourOrdersPath}>
        Your Orders
      </MenuItem>

      <MenuItem divider />

      {hasMultipleRegistries && (
        <MenuItem eventKey="5" href={urls.listsPath}>
          Switch Registry
        </MenuItem>
      )}
      <MenuItem eventKey="6" href={urls.logoutPath} onClick={trackLogOut}>
        Log Out
      </MenuItem>
    </DropdownButton>
  )
}
